import React, { useCallback, useEffect, useState } from 'react';
import { crmService } from '../../services/crm.service';
import { crmErrorMessage } from '../../utils/crm';
import { localDateString } from '../../utils/crmPipeline';
import LeadRow from './LeadRow';
import LeadActions from './LeadActions';
import TodayKpis from './TodayKpis';

const MAX_ROWS = 5;

const RANGES = [
  { value: 'week',   label: 'This week' },
  { value: 'month',  label: 'This month' },
  { value: 'custom', label: 'Custom…' },
];

const SIDE_FILTERS = [
  { value: 'all',  label: 'Buyers and sellers' },
  { value: 'BUY',  label: 'Buyers' },
  { value: 'SELL', label: 'Sellers' },
];

const matchesSide = (card, side) => side === 'all' || card.side === side;

const GROUPS = [
  { key: 'overdue',  title: 'Overdue follow-ups', empty: 'Nothing overdue.',    countTone: 'red'   },
  { key: 'dueToday', title: 'Due today',           empty: 'Nothing due today.',  countTone: 'amber' },
];

/**
 * What needs attention today: the KPI row, overdue + due-today lists with quick actions, a
 * collapsed "new leads not contacted" count, and a "going cold" footer line. "Today" is the
 * user's own calendar day, sent to the service, which has no time zone.
 */
const TodayView = ({ book, ownerUserId, reloadKey, onOpen, onOpenKpi }) => {
  const [range, setRange] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [side, setSide] = useState('all');
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [newLeads, setNewLeads] = useState({ total: 0, items: [] });
  const [showAll, setShowAll] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const today = localDateString();

  const reload = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    try {
      // "New leads not contacted" approximation: anyone whose lead is still at NEW stage is a
      // lead that has not progressed past first contact. If a tighter definition is needed later
      // (last-contact timestamp older than N hours), move it to the backend TodayResponse.
      const [todayData, newList] = await Promise.all([
        crmService.getToday(book, today, ownerUserId),
        crmService.listPersons(book, { ownerUserId, stage: 'NEW', size: MAX_ROWS }),
      ]);
      setState({ status: 'ready', data: todayData, error: '' });
      setNewLeads(newList);
    } catch (err) {
      setState({ status: 'error', data: null, error: crmErrorMessage(err) });
    }
  }, [book, ownerUserId, today]);

  useEffect(() => { reload(); }, [reload, reloadKey]);

  const act = useCallback(async (card, kind, snoozeDate) => {
    const key = `${card.personId}-${card.side}-${kind}`;
    setBusyKey(key);
    try {
      if (kind === 'call' || kind === 'email') {
        await crmService.logActivity(book, card.personId, {
          type: kind === 'call' ? 'CALL' : 'EMAIL',
          side: card.side,
        }, ownerUserId);
      } else {
        const tasks = await crmService.getTasks(book, card.personId, ownerUserId);
        const open = tasks.find((t) => t.status === 'OPEN');
        if (!open) return;
        const body = kind === 'done' ? { status: 'DONE' } : { dueDate: snoozeDate };
        await crmService.updateTask(book, card.personId, open.id, body, ownerUserId);
      }
      await reload();
    } catch (err) {
      setState((prev) => ({ ...prev, error: crmErrorMessage(err) }));
    } finally {
      setBusyKey(null);
    }
  }, [book, ownerUserId, reload]);

  if (state.status === 'error' && !state.data) return <p className="crm-error" role="alert">{state.error}</p>;
  if (!state.data) return <p className="crm-empty">Loading…</p>;

  const goingColdTotal = state.data.goingCold?.total || 0;
  const filteredNew = (newLeads.items || []).filter((p) => {
    if (side === 'all') return true;
    const hasBuy = p.buyerLead;
    const hasSell = p.sellerLead;
    return (side === 'BUY' && hasBuy) || (side === 'SELL' && hasSell);
  });

  return (
    <div className="crm-today">
      <div className="crm-today-filters">
        <select className="crm-select" aria-label="Date range" value={range} onChange={(e) => setRange(e.target.value)}>
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {range === 'custom' && (
          <>
            <input type="date" className="crm-input" aria-label="From" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="crm-note" style={{ margin: 0 }}>to</span>
            <input type="date" className="crm-input" aria-label="To" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        )}
        <select className="crm-select" aria-label="Buyer or seller" value={side} onChange={(e) => setSide(e.target.value)}>
          {SIDE_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <TodayKpis
        book={book}
        ownerUserId={ownerUserId}
        range={range}
        customFrom={customFrom}
        customTo={customTo}
        reloadKey={reloadKey}
        onOpenKpi={onOpenKpi}
      />

      {state.error && <p className="crm-error" role="alert">{state.error}</p>}

      {(state.data.overdue?.total || 0) === 0 && (state.data.dueToday?.total || 0) === 0
        && (newLeads.total || 0) === 0 && goingColdTotal === 0 && (
          <p className="crm-empty">You are all caught up. Nothing needs follow-up today.</p>
      )}

      {GROUPS.map((g) => {
        const group = state.data[g.key] || { total: 0, items: [] };
        const items = (group.items || []).filter((c) => matchesSide(c, side));
        const open = !!showAll[g.key];
        const shown = open ? items : items.slice(0, MAX_ROWS);
        return (
          <section key={g.key} className="crm-today-group" aria-label={g.title}>
            <div className="crm-today-group-head">
              <h2>{g.title} <span className={`crm-count crm-count-${g.countTone}`}>{items.length}</span></h2>
              {items.length > MAX_ROWS && (
                <button type="button" className="crm-link" onClick={() => setShowAll((a) => ({ ...a, [g.key]: !open }))}>
                  {open ? 'Show less' : `View all (${items.length})`}
                </button>
              )}
            </div>
            {shown.length === 0 ? (
              <p className="crm-note">{g.empty}</p>
            ) : (
              <ul className="crm-lead-list">
                {shown.map((card) => (
                  <LeadRow
                    key={`${card.personId}-${card.side}`}
                    card={card}
                    onOpen={onOpen}
                    today={today}
                    actions={(
                      <LeadActions
                        hasTask={!!card.nextTask}
                        busy={busyKey && busyKey.startsWith(`${card.personId}-${card.side}-`)}
                        onCall={() => act(card, 'call')}
                        onEmail={() => act(card, 'email')}
                        onDone={() => act(card, 'done')}
                        onSnooze={(d) => act(card, 'snooze', d)}
                      />
                    )}
                  />
                ))}
              </ul>
            )}
            {group.total > items.length && !open && (
              <p className="crm-note">Showing the first {items.length} of {group.total}.</p>
            )}
          </section>
        );
      })}

      <section className="crm-today-group" aria-label="New leads not contacted">
        <details className="crm-coll">
          <summary className="crm-today-group-head">
            <h2><span className="crm-car">▸</span> New leads not contacted <span className="crm-count">{filteredNew.length}</span></h2>
          </summary>
          {filteredNew.length === 0 ? (
            <p className="crm-note">All new leads have been contacted.</p>
          ) : (
            <ul className="crm-lead-list">
              {filteredNew.map((p) => (
                <li key={p.id} className="crm-lead-row">
                  <div className="crm-lead-body">
                    <div className="crm-lead-main">
                      <button type="button" className="crm-link" onClick={() => onOpen(p.id)}>
                        {[p.firstName, p.lastName].filter(Boolean).join(' ') || '(no name)'}
                      </button>
                    </div>
                    <div className="crm-lead-meta">Added {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {newLeads.total > filteredNew.length && (
            <p className="crm-note">Showing the first {filteredNew.length} of {newLeads.total}.</p>
          )}
        </details>
      </section>

      {goingColdTotal > 0 && (
        <p className="crm-note crm-going-cold">
          <strong>{goingColdTotal} {goingColdTotal === 1 ? 'lead' : 'leads'} going cold</strong>
          {' '}— no contact for 2 weeks and no follow-up planned.{' '}
          <button type="button" className="crm-link" onClick={() => onOpenKpi('cold')}>Review</button>
        </p>
      )}
    </div>
  );
};

export default TodayView;
